import type { Theme } from '@mui/material'
import type { FC } from 'react'
import type { ApprovalBatchStatusChange, ApprovalNote, NoteDTO, TimelineItem } from 'types/approvals'
import { EditOutlined } from '@ant-design/icons'
import { Box, Button, Divider, TextField, Typography } from '@mui/material'
import { useSubmitApprovalNote } from 'hooks/useApprovals'
import useAuth from 'hooks/useAuth'
import { useRef, useState, useTransition } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApprovalBatchStatus } from 'types/approvals'
import { formatDisplayDate } from 'types/periods'
import { ThemeMode } from 'types/config'

interface ApprovalNotesProps {
  batchId: string
  notes: ApprovalNote[]
  userNameMap: Record<string, string>
  history: ApprovalBatchStatusChange[]
  setNotes: (notes: ApprovalNote[]) => void
  isPlanApproval: boolean
  planUserId?: string
  planId?: string
  revision?: number
  isApproved: boolean
  theme: Theme
}

const ApprovalNotes: FC<ApprovalNotesProps> = ({
  batchId,
  notes,
  userNameMap,
  history,
  setNotes,
  isPlanApproval,
  planUserId,
  planId,
  revision,
  isApproved,
  theme,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isPending, startTransition] = useTransition()
  const [newNote, setNewNoteLocal] = useState('')
  const { token } = useAuth()
  const submitNote = useSubmitApprovalNote(token!)
  const notesEndRef = useRef<HTMLDivElement>(null)

  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.dark

  const handleAddNote = async () => {
    if (newNote.trim() !== '') {
      const noteDTO: NoteDTO = {
        batchId,
        content: newNote,
      }
      const updatedBatch = await submitNote(noteDTO)
      setNewNoteLocal('') // Clear the textarea after submitting
      setNotes(updatedBatch.notes) // Update notes with the response
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault() // Prevent the default action to avoid a newline in textarea
      handleAddNote()
    }
  }

  const lookupUserName = (userId: string) => {
    const userName = userNameMap[userId]
    if (!userName) {
      console.warn(`User ID ${userId} not found in userNameMap`)
      return 'System'
    }
    return userName
  }

  const formatDate = (timestamp: string) => {
    return formatDisplayDate(timestamp)
  }

  // Merge notes and history into unified timeline items, then sort by timestamp
  const timelineItems: TimelineItem[] = [
    ...notes.map((note) => ({
      ...note,
      isNote: true,
      userId: note.authorId,
    })),
    ...history.map((change) => ({
      ...change,
      isNote: false,
      userId: change.changedById, // Ensure userId is correctly mapped
    })),
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  // Clean up timeline items to remove redundant entries
  // Backend now handles most duplicates, but keep minimal frontend filtering for UX
  const filteredTimelineItems = timelineItems.filter((item, index, array) => {
    // Filter out "Auto-Approved" notes when followed by approval status changes
    if (item.isNote && item.content && item.content.includes('Auto-Approved')) {
      // Check if next item is an approval status change
      if (index < array.length - 1) {
        const nextItem = array[index + 1]
        if (!nextItem.isNote) {
          const nextStatusChange = nextItem as unknown as ApprovalBatchStatusChange
          if (nextStatusChange.newStatus === ApprovalBatchStatus.APPROVED) {
            return false // Hide Auto-Approved note
          }
        }
      }
    }

    // Filter out status changes with empty actions that aren't meaningful
    if (!item.isNote) {
      const statusChange = item as unknown as ApprovalBatchStatusChange

      // Remove duplicate consecutive status changes with same status and user
      if (index > 0) {
        const prevItem = array[index - 1]
        if (!prevItem.isNote) {
          const prevStatusChange = prevItem as unknown as ApprovalBatchStatusChange
          if (
            statusChange.newStatus === prevStatusChange.newStatus &&
            statusChange.changedById === prevStatusChange.changedById &&
            statusChange.newStatus === ApprovalBatchStatus.APPROVED
          ) {
            return false // Hide duplicate approval
          }
        }
      }

      // Remove ITEM_UPDATED status changes that immediately follow related plan notes
      if (statusChange.newStatus === ApprovalBatchStatus.ITEM_UPDATED) {
        // Check if previous item is a plan creation/update note
        if (index > 0) {
          const prevItem = array[index - 1]
          if (
            prevItem.isNote &&
            prevItem.content &&
            (prevItem.content.includes('updated compensation plan') ||
              prevItem.content.includes('created compensation plan'))
          ) {
            return false
          }
        }
      }
    }

    return true
  })

  const handleEditPlan = () => {
    startTransition(() => {
      navigate(`/create-comp-plan?userId=${planUserId}&planId=${planId}&revision=${revision}`, {
        state: { from: location.pathname },
      })
    })
  }

  return (
    <div style={{ maxHeight: '100%', overflowY: 'auto' }}>
      {isPlanApproval && (
        <Button
          variant="text"
          fullWidth
          sx={{ mb: 2, '&:hover': { backgroundColor: hoverBackgroundColor } }}
          onClick={handleEditPlan}
          disabled={isPending}
          startIcon={<EditOutlined />}
        >
          {isPending ? 'Loading...' : 'Edit Plan'}
        </Button>
      )}
      {filteredTimelineItems.map((item, index) => {
        const isNote = item.isNote
        const authorName = isNote ? lookupUserName(item.userId ?? '') : 'System'
        const formattedTimestamp = formatDate(item.timestamp)
        const content = isNote ? item.content : `Status changed to ${item.newStatus}`

        if (isNote && content) {
          const isPlanCreationNote = content.includes('created compensation plan')
          const isPlanUpdateNote = content.includes('updated compensation plan')

          if (isPlanCreationNote || isPlanUpdateNote) {
            return (
              <Box key={index}>
                <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} mr={2}>
                  <Box textAlign="right">
                    <Typography variant="body2" color="textSecondary">
                      {content}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formattedTimestamp}
                    </Typography>
                  </Box>
                </Box>
                {index < filteredTimelineItems.length - 1 && !filteredTimelineItems[index + 1].isNote && (
                  <Divider sx={{ my: 2 }} />
                )}
              </Box>
            )
          }

          return (
            <Box key={index}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} mr={2}>
                <Box textAlign="right">
                  <Typography variant="body2" color="primary.main" fontWeight="medium">
                    {authorName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {content}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formattedTimestamp}
                  </Typography>
                </Box>
              </Box>
              {index < filteredTimelineItems.length - 1 && filteredTimelineItems[index + 1].isNote && (
                <Divider sx={{ my: 2 }} />
              )}
            </Box>
          )
        } else {
          const getActionText = (statusChange: ApprovalBatchStatusChange) => {
            const changedBy = lookupUserName(statusChange.changedById ?? '')

            switch (statusChange.newStatus) {
              case ApprovalBatchStatus.INITIAL:
                return 'Ready for review'
              case ApprovalBatchStatus.PENDING:
                return statusChange.action === 'approved' ? `Approved by ${changedBy}` : 'Items pending approval'
              case ApprovalBatchStatus.APPROVED:
                return `Approved by ${changedBy}`
              case ApprovalBatchStatus.REJECTED:
                return `Rejected by ${changedBy}`
              case ApprovalBatchStatus.INVALID:
                return 'Batch no longer valid'
              case ApprovalBatchStatus.CHANGES_REQUESTED:
                return `Changes requested by ${changedBy}`
              case ApprovalBatchStatus.ITEM_UPDATED:
                return 'Item updated'
              default:
                return `Status changed to ${statusChange.newStatus}`
            }
          }

          const actionText = getActionText(item as unknown as ApprovalBatchStatusChange)

          return (
            <Box key={index}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} mr={2}>
                <Box textAlign="right">
                  <Typography variant="body2" color="textSecondary">
                    {actionText}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formattedTimestamp}
                  </Typography>
                </Box>
              </Box>
              {index < filteredTimelineItems.length - 1 && filteredTimelineItems[index + 1].isNote && (
                <Divider sx={{ my: 2 }} />
              )}
            </Box>
          )
        }
      })}
      <div ref={notesEndRef} />
      <TextField
        multiline
        minRows={3}
        fullWidth
        variant="outlined"
        value={newNote}
        onChange={(e) => setNewNoteLocal(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add a new note..."
        InputProps={{
          sx: {
            padding: '8px',
            borderRadius: '12px',
          },
        }}
      />
    </div>
  )
}

export default ApprovalNotes
